/**
 * @module companies
 */
import { createReducer } from '@reduxjs/toolkit';
import { taskActions } from '~/behaviors/actions/task.actions';
import { taskTemplateActions } from '~/behaviors/actions/task_template.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { employeeAttributeActions } from '~/behaviors/actions/employee_attribute.actions';
import { companyActions } from '~/behaviors/actions/company.actions';
import { accountActions } from '~/behaviors/actions/account.actions';
import { list } from '~/tools/list';
import { benchmarkActions } from '~/behaviors/actions/benchmark.actions';
import { CompaniesDict } from '~/interfaces/company.interface';
import { handleUnsafeOptionalChaining } from '~/tools/lint/handleUnsafeOptionalChaining';

const defaultState = {};

const reset = () => defaultState;

const uniqueSupportedLanguages = (existingLangs, newLangs) => {
  return [...newLangs, ...existingLangs].filter(list.onlyUnique);
};

/**
 * Whenever the user's account is updated, refresh the corresponding entity as well.
 */
const handleAccountChange = (state, action) => {
  const {
    companyId,
    companyName,
    companyLogoUrl,
    companyColor,
    supportedLanguages: newLangs,
  } = handleUnsafeOptionalChaining(action?.payload?.account);
  const company = action?.payload?.entities?.companies[companyId];
  const existingLangs = company?.supportedLanguages || [];
  const supportedLanguages = uniqueSupportedLanguages(existingLangs, newLangs);

  // Preserve fields owned by the company list / metadata queries (e.g. the
  // per-company counts populated by fetchCompaniesMetadata). The account's
  // companyFrom transformer always emits those count keys, but they come
  // through as `undefined` here because the account payload doesn't carry
  // them. Dropping undefined values keeps them from clobbering the existing
  // entity, so the counts survive a company switch.
  const definedCompanyFields = Object.fromEntries(
    Object.entries(company || {}).filter(([_, value]) => value !== undefined),
  );

  state[companyId] = {
    ...state[companyId],
    ...definedCompanyFields,
    id: companyId,
    name: companyName,
    logoUrl: companyLogoUrl,
    color: companyColor,
    supportedLanguages,
  };
};

/**
 * Iterate over each element of `action.payload.companies` and merge each with any
 * existing entity with the same ID in the state (or create new one if none exist).
 */
const mergeCompanies = (state, action) => {
  const companies: CompaniesDict = action?.payload?.entities?.companies || [];

  Object.values(companies).forEach((company) => {
    const newLangs = company.supportedLanguages || [];
    const existingLangs = state[company.id]?.supportedLanguages || [];
    const supportedLanguages = uniqueSupportedLanguages(existingLangs, newLangs);

    state[company.id] = {
      ...state[company.id],
      ...company,
      supportedLanguages,
    };
  });
};

const addEmployeeAttribute = (state, action) => {
  const employeeAttributeId = action?.payload?.employeeAttribute?.id;
  const companyId = action?.payload?.companyId;

  state[companyId] = {
    ...state[companyId],
    employeeAttributes: [...state[companyId].employeeAttributes, employeeAttributeId],
  };
};

const deleteEmployeeAttribute = (state, action) => {
  const employeeAttributeId = action?.payload?.employeeAttributeId;
  const companyId = action?.payload?.companyId;
  const updatedCompanyEmployeeAttributes = state[companyId].employeeAttributes;
  const attrIndex = updatedCompanyEmployeeAttributes.indexOf(employeeAttributeId);

  updatedCompanyEmployeeAttributes.splice(attrIndex, 1);

  state[companyId] = {
    ...state[companyId],
    employeeAttributes: updatedCompanyEmployeeAttributes,
  };
};

const updateLogo = (state, action) => {
  if (action.payload.logoUrl) state.logoUrl = action.payload.logoUrl;
};

const removeCompany = (state, action) => {
  const companyId = action?.payload?.companyId;
  const { [companyId]: company, ...rest } = state;

  return rest;
};

const companies = createReducer(defaultState, (builder) => {
  builder
    .addCase(metaActions.reset, reset)
    .addCase(accountActions.logout.pending, reset)
    .addCase(accountActions.fetchAccount.fulfilled, handleAccountChange)
    .addCase(taskActions.fetchTaskList.fulfilled, mergeCompanies)
    .addCase(taskActions.fetchTask.fulfilled, mergeCompanies)
    .addCase(taskTemplateActions.fetchTaskTemplateList.fulfilled, mergeCompanies)
    .addCase(taskTemplateActions.fetchTaskTemplate.fulfilled, mergeCompanies)
    .addCase(companyActions.createCompany.fulfilled, mergeCompanies)
    .addCase(companyActions.fetchCompanyList.fulfilled, mergeCompanies)
    .addCase(companyActions.fetchCompaniesMetadata.fulfilled, mergeCompanies)
    .addCase(companyActions.fetchCompany.fulfilled, mergeCompanies)
    .addCase(companyActions.fetchCompanyKioskSettings.fulfilled, mergeCompanies)
    .addCase(employeeAttributeActions.fetchEmployeeAttributes.fulfilled, mergeCompanies)
    .addCase(companyActions.updateCompanyKioskSettings.fulfilled, mergeCompanies)
    .addCase(companyActions.modifyCompany.fulfilled, mergeCompanies)
    .addCase(companyActions.changeLogo.fulfilled, updateLogo)
    .addCase(employeeAttributeActions.createEmployeeAttribute.fulfilled, addEmployeeAttribute)
    .addCase(employeeAttributeActions.removeEmployeeAttribute.fulfilled, deleteEmployeeAttribute)
    .addCase(companyActions.deleteCompany.fulfilled, removeCompany)
    .addCase(benchmarkActions.fetchCompanyEmployeeAttributes.fulfilled, mergeCompanies);
});

export default companies;
